<script lang="ts">
  import AdminDebugger from "./AdminDebugger.svelte";
  import Spinner from "$components/forms/Spinner.svelte";
  import { page } from "$app/stores";
  import { slide } from "svelte/transition";
  import { colors, activeColor } from "$lib/data";
  import { navigating } from "$app/stores";
  import { section } from "./section";
  import { dev } from "$app/environment";
  import { get_pattern } from "$lib/utilities";

  // icons
  import {
    GearSix,
    Wrench,
    User,
    ImageSquare,
    YoutubeLogo,
    StarFour,
    Calendar,
    Video,
    Key,
    SquaresFour,
    Rocket,
    Binoculars,
    EnvelopeSimple,
    ChatsCircle,
    Download,
    Book,
    CaretDown,
  } from "phosphor-svelte";

  export let mobile_pad: boolean = false;
  export let hide_if_small: boolean = false;

  // we need to setup special checks for the special access menu links
  $: guest_crafter = !!$page.data.user?.as_guest;
  $: spin_sponsor = !!$page.data.user?.spin_sponsorships?.length;

  $: if (dev) {
    console.log("guest_crafter", guest_crafter);
    console.log("spin_sponsor", spin_sponsor);
  }

  // we need a function that can handle advanced show logic for items in the menu..
  const should_show_item = (item: any) => {
    // if the user is admin, they can see everything
    if ($page.data.user?.role === "admin") {
      return true;
    }
    if (item.required_special) {
      if (item.required_special.includes("guest-crafter") && guest_crafter) {
        return true;
      }
      if (item.required_special.includes("spin-sponsor") && spin_sponsor) {
        return true;
      }
      return false;
    }
    return true;
  };

  const base_url = `/members`;
  const preload = false;

  const admin_menu = [
    {
      title: "Admin Tools",
      href: `${base_url}/admin`,
      section: "admin",
      icon: Wrench,
      admin_required: true,
    },
  ];

  const dashboard_menu_data = [
    {
      title: "Dashboard",
      href: base_url,
      section: "dashboard",
      required_support: 0,
      icon: GearSix,
      sub_menu: [
        {
          title: "Home",
          href: base_url,
          required_support: 0,
        },
        {
          title: "Direct Message / Inbox",
          href: `${base_url}/inbox`,
          required_support: 0,
        },
        {
          title: "Update Blog",
          href: `${base_url}/updates`,
          required_support: 1,
        },
        {
          title: "Account Settings",
          href: `${base_url}/settings`,
          required_support: 0,
        },
        {
          title: "My Subscription",
          href: `${base_url}/subscription`,
          required_support: 0,
        },
        {
          title: "Notifications",
          href: `${base_url}/notifications`,
          required_support: 1,
        },
        {
          title: "Help & Documentation",
          href: `${base_url}/help`,
          required_support: 0,
        },
      ],
    },
    {
      is_sub_head: true,
      title: "Special Access",
      required_special: ["guest-crafter", "spin-sponsor"],
    },
    {
      title: "Guest Crafter Resources",
      href: `${base_url}/guest-crafter`,
      section: "guest-crafter",
      required_special: ["guest-crafter"],
      required_support: 0,
      icon: User,
      sub_menu: [
        {
          title: "Downloads",
          href: `${base_url}/guest-crafter`,
          required_support: 0,
        },
        {
          title: "Show Schedule",
          href: `${base_url}/guest-crafter/schedule`,
          required_support: 0,
        },
        {
          title: "Profile Update",
          href: `${base_url}/guest-crafter/profile`,
          required_support: 0,
        },
        {
          title: "Profile Image",
          href: `${base_url}/guest-crafter/image`,
          required_support: 0,
        },
      ],
    },
    {
      title: "Spin Sponsor Details",
      href: `${base_url}/spin-sponsor`,
      required_special: ["spin-sponsor"],
      section: "spin-sponsor",
      required_support: 0,
      icon: User,
    },
    {
      title: "Media Packs & Promo",
      href: `${base_url}/media-packs`,
      section: "media-packs",
      required_special: ["guest-crafter", "spin-sponsor"],
      required_support: 0,
      icon: User,
    },
    {
      is_sub_head: true,
      title: "My Content",
    },
    {
      title: "Public Profile",
      href: `${base_url}/profile`,
      section: "profile",
      required_support: 0,
      icon: User,
      sub_menu: [
        {
          title: "My Profile",
          href: `${base_url}/profile`,
          required_support: 1,
        },
        {
          title: "Pin Drop",
          href: `${base_url}/profile/pin`,
          required_support: 1,
        },
        {
          title: "Profile Image",
          href: `${base_url}/profile/image`,
          required_support: 1,
        },
      ],
    },
    {
      title: "My Contributions",
      href: `${base_url}/contributions`,
      section: "contributions",
      icon: ImageSquare,
      required_support: 1,
      bypass_if_special: true,
      sub_menu: [
        {
          title: "My Contributions",
          href: `${base_url}/contributions`,
          required_support: 1,
          bypass_if_special: true,
        },
        {
          title: "Submit",
          href: `${base_url}/contributions/submit`,
          required_support: 1,
          bypass_if_special: true,
        },
        {
          title: "Claim",
          href: `${base_url}/contributions/claim`,
          required_support: 1,
          bypass_if_special: true,
        },
      ],
    },
    {
      title: "Process Videos",
      href: `${base_url}/process-videos`,
      section: "process-videos",
      icon: YoutubeLogo,
      required_support: 1,
      sub_menu: [
        {
          title: "My Process Videos",
          href: `${base_url}/process-videos`,
          required_support: 1,
        },
        {
          title: "Submit Process Video",
          href: `${base_url}/process-videos/submit`,
        },
      ],
    },
    {
      title: "Galleries",
      href: `${base_url}/galleries`,
      section: "galleries",
      icon: SquaresFour,
      required_support: 1,
      sub_menu: [
        {
          title: "My Galleries",
          href: `${base_url}/galleries`,
          required_support: 1,
        },
        {
          title: "Create New Gallery",
          href: `${base_url}/galleries/create`,
          required_support: 1,
        },
      ],
    },
    {
      is_sub_head: true,
      title: "Community",
    },
    {
      title: "Bonus Challenge",
      href: `${base_url}/challenge`,
      section: "challenge",
      icon: Rocket,
      required_support: 1,
      sub_menu: [
        {
          title: "Current Challenge",
          href: `${base_url}/challenge`,
          required_support: 1,
        },
        {
          title: "Submit Bonus",
          href: `${base_url}/contributions/submit/bonus`,
          required_support: 1,
        },
        {
          title: "Past Challenges",
          href: `${base_url}/challenge/past`,
          required_support: 1,
        },
        {
          title: "Top 10",
          href: `${base_url}/challenge/top-ten`,
          required_support: 1,
        },
        {
          title: "Recent",
          href: `${base_url}/challenge/recent`,
          required_support: 1,
        },
      ],
    },
    {
      title: "Contribution Explorer",
      href: `${base_url}/explorer`,
      icon: Binoculars,
      section: "explorer",
      required_support: 1,
    },
    {
      title: "Card Exchange",
      icon: EnvelopeSimple,
      href: `${base_url}/exchange`,
      section: "exchange",
      required_support: 1,
      sub_menu: [
        {
          title: "Exchange List",
          href: `${base_url}/exchange`,
          required_support: 1,
        },
        {
          title: "My Commitments",
          href: `${base_url}/exchange/commitments`,
          required_support: 1,
        },
        {
          title: "Birthdays",
          href: `${base_url}/exchange/birthdays`,
          required_support: 1,
        },
        {
          title: "Settings",
          href: `${base_url}/exchange/settings`,
          required_support: 1,
        },
        {
          title: "Help",
          href: `${base_url}/exchange/help`,
          required_support: 1,
        },
      ],
    },
    {
      title: "Discussions",
      href: `${base_url}/discussions`,
      section: "discussions",
      icon: ChatsCircle,
      required_support: 1,
      sub_menu: [
        {
          title: "Video Discussions",
          href: `${base_url}/discussions`,
          required_support: 1,
        },
        {
          title: "Update Discussions",
          href: `${base_url}/discussions/update-discussions`,
          required_support: 1,
        },
        {
          title: "My Comments",
          href: `${base_url}/discussions/my-comments`,
          required_support: 1,
        },
      ],
    },
    {
      title: "Surveys & Vetos",
      href: `${base_url}/surveys`,
      icon: StarFour,
      section: "surveys",
      required_support: 1,
    },
    {
      title: "Zoom Schedule",
      href: `${base_url}/zoom`,
      section: "zoom-schedule",
      icon: Calendar,
      required_support: 1,
    },
    {
      is_sub_head: true,
      title: "Resources",
    },
    {
      title: "Video Library",
      href: `${base_url}/videos`,
      section: "videos",
      icon: Video,
      required_support: 1,
      sub_menu: [
        {
          title: "Feed",
          href: `${base_url}/videos`,
          required_support: 1,
        },
        {
          title: "Search",
          href: `${base_url}/videos/search`,
          required_support: 1,
        },
        {
          title: "Members Show",
          href: `${base_url}/videos/show`,
          required_support: 1,
        },
        {
          title: "Mary's Laboratory",
          href: `${base_url}/videos/laboratory`,
          required_support: 1,
        },
        {
          title: "Scrapbooking",
          href: `${base_url}/videos/scrapbooking`,
          required_support: 1,
        },
      ],
    },
    {
      title: "Parameter Vault",
      href: `${base_url}/parameters`,
      section: "parameters",
      icon: Key,
      required_support: 1,
      sub_menu: [
        {
          title: "Projects",
          href: `${base_url}/parameters`,
          required_support: 1,
        },
        {
          title: "Colors",
          href: `${base_url}/parameters/colors`,
          required_support: 1,
        },
        {
          title: "Elements",
          href: `${base_url}/parameters/elements`,
          required_support: 1,
        },
        {
          title: "Random",
          href: `${base_url}/parameters/random`,
          required_support: 1,
        },
      ],
    },
    {
      title: "Downloads",
      href: `${base_url}/downloads`,
      section: "downloads",
      icon: Download,
      required_support: 1,
      sub_menu: [
        {
          title: "All Downloads",
          href: `${base_url}/downloads`,
          required_support: 1,
        },
        {
          title: "Coloring Sheets",
          href: `${base_url}/downloads/sheets`,
          required_support: 1,
        },
        {
          title: "Digital Stamps",
          href: `${base_url}/downloads/stamps`,
          required_support: 1,
        },
        {
          title: "Card Templates",
          href: `${base_url}/downloads/cards`,
          required_support: 1,
        },
      ],
    },
    {
      title: "Scrapbooking",
      href: `${base_url}/scrapbooking`,
      icon: Book,
      section: "scrapbooking",
      required_support: 3,
      sub_menu: [
        {
          title: "Links & Content",
          href: `${base_url}/scrapbooking`,
          required_support: 3,
        },
        {
          title: "Life Saver E-Book",
          href: `${base_url}/scrapbooking/life-saver`,
          required_support: 3,
        },
      ],
    },
  ];
</script>

<aside
  style="
        --primary: {colors[$activeColor].color};
        --secondary: {colors[$activeColor].highlight};
    "
  class:hide_if_small
  class:mobile_pad
>
  <div class="hold">
    <div class="header" style:background={get_pattern()}>Member Content</div>

    <nav>
      {#if $page.data.user?.role === "admin"}
        {#each admin_menu as item}
          <div class="item">
            <a
              data-sveltekit-preload-data={preload}
              href={item.href}
              class:active={$page.url.pathname === item.href}
              on:click={() => ($section = item.section ?? "dashboard")}
            >
              <svelte:component
                this={item.icon}
                size="1.1rem"
                color={$section === item.section
                  ? `var(--primary)`
                  : `var(--secondary)`}
              />
              <div class="text">{item.title}</div>
              {#if $section === item.section && $navigating}
                <div class="spin">
                  <Spinner size_to_container invert_colors tiny />
                </div>
              {/if}
            </a>
          </div>
        {/each}
      {/if}
      {#each dashboard_menu_data as item}
        {#if item.is_sub_head && should_show_item(item)}
          <div class="sub-head" style:background={get_pattern()}>
            {item.title}
          </div>
        {:else if item.section && should_show_item(item)}
          <div class="item">
            <a
              data-sveltekit-preload-data={preload}
              href={item.href}
              class:active={$page.url.pathname === item.href}
              on:click={() => ($section = item.section ?? "dashboard")}
              class:disabled={$page.data.user?.support_level <
                item.required_support &&
                !(item.bypass_if_special && guest_crafter)}
            >
              <svelte:component
                this={item.icon}
                size="1.1rem"
                color={$section === item.section
                  ? `var(--primary)`
                  : `var(--secondary)`}
              />
              <div class="text">{item.title}</div>
              {#if $section === item.section && $navigating}
                <div class="spin">
                  <Spinner size_to_container invert_colors tiny />
                </div>
              {:else if item.sub_menu}
                <button
                  class="arrow"
                  on:click|preventDefault|stopPropagation={() =>
                    ($section = item.section ?? "dashboard")}
                  class:active={$section === item.section}
                >
                  <CaretDown size="1rem" color="var(--offDark)" />
                </button>
              {/if}
            </a>
            {#if item.sub_menu && $section === item.section}
              <div class="sub last" transition:slide>
                {#each item.sub_menu as sub_item}
                  <a
                    href={sub_item.href}
                    on:click={() => ($section = item.section ?? "dashboard")}
                    class:active={$page.url.pathname === sub_item.href}
                  >
                    {sub_item.title}
                  </a>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
      {/each}
    </nav>

    {#if dev}
      <AdminDebugger />
    {/if}
  </div>
</aside>

<style>
  .sub-head {
    color: var(--offDark);
    font-size: 0.8rem;
    font-weight: bold;
    padding: 0.6rem 1rem 0.5rem 1rem;
    border-bottom: 1px solid var(--dots);
  }

  aside {
    width: 25%;
    background: white;
    box-shadow: var(--shadow);
    flex-shrink: 0;
    padding-bottom: 3rem;
    position: relative;
  }
  aside.mobile_pad {
    padding-bottom: 6rem;
  }
  aside .hold {
    position: sticky;
    top: 50px;
  }
  aside.hide_if_small {
    border-radius: 1rem;
  }
  @media (max-width: 1024px) {
    aside {
      width: 100%;
    }
  }
  aside .header {
    overflow: hidden;
    border-radius: 1rem 1rem 0 0;
    font-size: 1.2rem;
    padding: 1.9rem 1rem 1.3rem 1rem;
    color: var(--title);
    text-shadow: -3px -3px white;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }
  aside nav {
    display: flex;
    flex-flow: column;
    border-top: 1px solid var(--dots);
  }
  @media (max-width: 1024px) {
    aside nav {
      margin-top: 0;
      padding-bottom: 0.25rem;
    }
  }
  aside nav .sub a {
    padding: 0.4rem 1rem 0.4rem 4.1rem;
    font-size: 0.8rem;
    border: none;
    color: #516a7a;
  }
  aside nav .sub a:hover {
    color: var(--secondary);
  }
  aside nav .sub a.active {
    color: var(--title);
    border-left: 5px solid var(--primary);
    gap: 0.75rem;
    padding: 0.4rem 1rem 0.4rem 4.1rem;
  }
  aside nav .sub a.active:hover {
    color: var(--primary);
  }
  aside nav a {
    padding: 0.5rem 1.5rem;
    border-bottom: 1px solid var(--dots);
    text-decoration: none;
    font-size: 0.95rem;
    color: var(--title);
    display: flex;
    gap: 1rem;
    align-items: center;
  }
  aside nav a .spin {
    width: 1rem;
    height: 1rem;
  }
  aside nav a .text {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  aside nav a:hover,
  aside nav a.active {
    color: var(--secondary);
  }
  aside nav a .arrow {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  aside nav a.active {
    border-left: 5px solid var(--primary);
    gap: 0.75rem;
    padding-left: 1.5rem;
  }
  aside nav a.disabled {
    color: var(--offDark);
  }

  @media (max-width: 1024px) {
    aside.hide_if_small {
      display: none;
    }
  }
  aside nav .sub {
    border-bottom: 1px solid var(--dots);
  }
</style>
